import { useMemo } from 'react';
import {
	USE_CASES_NTH_USER_ONBOARDING,
	ONBOARDING_EXPERIENCES,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { fg } from '@confluence/feature-gating';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsEligibleForNthUserHomeOnboardingLicensedUserCount } from './useIsEligibleForNthUserHomeOnboardingLicensedUserCount.ts';
import { isWithinLast14Days } from '../use-cases-in-nth-user-onboarding/utils/isWithinLast14Days.ts';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';

export const useIsEligibleForUseCasesOnboardingHomeChanges = (): {
	loading: boolean;
	isEligible: boolean;
	error?: Error;
} => {
	const {
		data,
		loading: onboardingLoading,
		error,
	} = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			USE_CASES_NTH_USER_ONBOARDING.VIEWED_AT,
			USE_CASES_NTH_USER_ONBOARDING.DISMISSED_HEADER,
		],
		!fg('use-cases-onboarding-state-retrieval-kill-switch'),
	);

	const { isFoundingAdmin, viewedAt, headerDismissed } = deserializeState(data);
	const isNthUser = !onboardingLoading && !error && !isFoundingAdmin;

	const {
		licensedUserCount,
		loading: userCountDataLoading,
		error: userCountDataError,
	} = useIsEligibleForNthUserHomeOnboardingLicensedUserCount(
		!isNthUser || !fg('cc-activation-unify-home-onboarding-kill-switch'),
	);

	const { isNewUser, loading: isNewUserLoading } = useIsNewUser(
		{},
		!isNthUser || !fg('cc-activation-unify-home-onboarding-kill-switch'),
	);

	const isOverTenUserInstance = Boolean(licensedUserCount && licensedUserCount > 10);

	const isUnifyHomeOnboardingExperimentEligible =
		!userCountDataError &&
		!userCountDataLoading &&
		!isNewUserLoading &&
		isOverTenUserInstance &&
		isNewUser &&
		fg('cc-activation-unify-home-onboarding-kill-switch');

	const isWithinValidTimeWindow = useMemo(() => {
		if (
			isUnifyHomeOnboardingExperimentEligible &&
			expValEquals('cc-activation-unify-home-onboarding-nth-user', 'cohort', 'experiment')
		) {
			// Experiment users: Since they are not exposed to Account Setup Scrren viewedAt is set to null.
			return !viewedAt || viewedAt.trim() === '' || isWithinLast14Days(viewedAt);
		}
		// Regular users: must be within 14 days
		return isWithinLast14Days(viewedAt);
	}, [viewedAt, isUnifyHomeOnboardingExperimentEligible]);

	// We only want to show useCasesInNthUserOnboarding if we are within the 14 days onboarding period
	// and the header has not already been dismissed.
	const isEligibleForUseCasesHomeChanges = isWithinValidTimeWindow && !headerDismissed && !error;

	return {
		loading: onboardingLoading || userCountDataLoading || Boolean(isNewUserLoading),
		isEligible: isEligibleForUseCasesHomeChanges,
		error: error || userCountDataError,
	};
};
